import { useEffect, useState } from 'react'
import { todayISO } from '../data/workdate.js'
import './workday.css'
import './recheckin.css'

import backChevron from '../assets/figma/workday/back-chevron.svg'
import dotCheckedOut from '../assets/figma/workday/dot-checkedout.svg'
import radioOn from '../assets/figma/workday/radio-selected.svg'
import radioOff from '../assets/figma/workday/radio-unselected.svg'
import lockIcon from '../assets/figma/workday/governance-lock.svg'

/*
 * State B — Reasoned Re-Check-In, implemented from the approved Figma
 * prototype frame 9:58. Structure, spacing, type and colour are the Figma
 * values — see recheckin.css.
 *
 * LOCKED FOUNDER DECISION (9:117), supersedes the earlier Alpha rule ("just
 * check in again"): a successful Check-Out is ALWAYS preserved. This screen
 * therefore only collects a reason. It writes nothing and rewrites nothing;
 * the earlier check-out is displayed above, read-only, exactly as recorded.
 *
 * BUILD 001 (S6) adds the project confirmation the brief requires. It is NOT
 * added to this frame — choosing the reason advances to the existing Check-In
 * project screen, which prefills today's assignment and leaves the list open
 * to change. Figma 9:58 is therefore implemented unmodified.
 */

// 9:89 / 9:94 / 9:98 / 9:102 / 9:106 — the approved reasons, in Figma order.
const REASONS = [
  { id: 'mistake',   label: 'Checked out by mistake' },
  { id: 'returned',  label: 'Returned to finish work' },
  { id: 'asked',     label: 'Asked to return' },
  { id: 'emergency', label: 'Emergency or service call' },
  { id: 'other',     label: 'Other' },
]

export default function ReCheckIn({ adapter, session, onHome, onReason }) {
  // 9:89 shows the first reason preselected — the inference is stated, not
  // asked (Employee Home Experience v1.0 A6). Any other choice is one tap.
  const [chosen, setChosen] = useState('mistake')
  const [otherText, setOtherText] = useState('')
  const [workday, setWorkday] = useState(null)

  const employeeId = session?.employee?.id ?? null
  useEffect(() => {
    let alive = true
    if (typeof adapter.getWorkdayForDate !== 'function') return
    adapter.getWorkdayForDate(employeeId, todayISO(), session?.employee?.name ?? null)
      .then((w) => { if (alive) setWorkday(w) })
      .catch(() => { /* the screen still works without the summary */ })
    return () => { alive = false }
  }, [adapter, employeeId, session?.employee?.name])

  const segments = workday?.segments ?? []
  const last = segments[segments.length - 1] ?? null
  // 9:111 — free text is required ONLY when Other is chosen.
  const blocked = chosen === 'other' && !otherText.trim()

  function submit() {
    if (blocked) return
    const reason = REASONS.find((r) => r.id === chosen)
    onReason({
      id: chosen,
      label: reason?.label ?? chosen,
      text: chosen === 'other' ? otherText.trim() : null,
    })
  }

  return (
    <div className="wd" data-figma="9:58">
      <div className="wd-header">
        <button type="button" className="wd-back" onClick={onHome}>
          <img className="wd-back__icon" src={backChevron} alt="" />
          Home
        </button>
        <p className="wd-title">WORKDAY</p>
        <div className="wd-header__spacer" />
      </div>

      <div className="wd-body wd-body--rc">
        {/* Checked out card — 9:77. The check-out being preserved, shown as
            recorded. Read-only: nothing here can edit it. */}
        <div className="wd-card wd-card--out" data-figma="9:77">
          <div className="wd-card__label">
            <img className="wd-card__dot" src={dotCheckedOut} alt="" />
            <p className="wd-card__status">CHECKED OUT</p>
          </div>
          <p className="wd-card__line">
            {last?.reportedProjectName ?? session?.project?.name ?? 'Project not yet matched'}
          </p>
          <p className="wd-card__line wd-card__line--muted">
            {timeLabel(segments[0]?.checkedInAt)} &ndash; {timeLabel(last?.checkedOutAt)}
          </p>
        </div>

        <div className="rc-gap-22" />
        <p className="rc-title" data-figma="9:84">Checking in again today</p>
        <div className="rc-gap-5" />
        <p className="rc-sub" data-figma="9:86">
          Choose a reason. This keeps the original check-out intact.
        </p>
        <div className="rc-gap-14" />

        <div className="rc-choices" role="radiogroup" aria-label="Reason for checking in again"
             data-figma="9:88">
          {REASONS.map((r) => (
            <button
              key={r.id}
              type="button"
              role="radio"
              aria-checked={chosen === r.id}
              className={`rc-choice${chosen === r.id ? ' rc-choice--on' : ''}`}
              onClick={() => setChosen(r.id)}
            >
              <img className="rc-choice__radio" src={chosen === r.id ? radioOn : radioOff} alt="" />
              <span className="rc-choice__label">{r.label}</span>
            </button>
          ))}
        </div>

        {chosen === 'other' && (
          <textarea
            className="rc-other"
            rows={2}
            placeholder="In your own words…"
            aria-label="Reason for checking in again"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            autoFocus
          />
        )}

        <div className="rc-gap-10" />
        <p className="rc-foot" data-figma="9:111">
          Free text is required only if Other is chosen.
        </p>

        <div className="wd-grow" />

        {/* Governance caveat — 9:113 */}
        <div className="rc-caveat" data-figma="9:113">
          <img className="rc-caveat__icon" src={lockIcon} alt="" />
          <p className="rc-caveat__text">
            Locked Founder Decision — supersedes the earlier Alpha rule (&ldquo;just check in
            again&rdquo;). Successful Check-Out is always preserved.
          </p>
        </div>
        <div className="rc-gap-12" />

        <button type="button" className="rc-cta" data-figma="9:119"
                disabled={blocked} onClick={submit}>
          Check In
        </button>
      </div>
    </div>
  )
}

function timeLabel(iso) {
  if (!iso) return '--:-- --'
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) }
  catch { return '--:-- --' }
}
